import React, { useState } from 'react';
import { CheckCircle2, Copy, Check, Building2, Hash, User, Wallet, Info, X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';

/**
 * Displayed after a successful investment application. Shows Daye Farm Ltd's
 * bank account + the unique payment reference. Same details are emailed via
 * the `investment_payment_instructions` template (idempotent, so users always
 * have a record).
 */
const CopyableRow = ({ icon: Icon, label, value, mono = false }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — please copy manually.');
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border">
      <div className="flex items-center gap-3 min-w-0">
        <Icon className="h-4 w-4 text-green-700 flex-shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
          <p className={`font-semibold truncate ${mono ? 'font-mono text-lg' : ''}`}>{value}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={copy}
        className="text-green-700 hover:text-green-800 flex-shrink-0 p-1.5 rounded hover:bg-green-50"
        aria-label={`Copy ${label}`}
        data-testid={`copy-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
};

const PaymentInstructions = ({ instructions, onClose }) => {
  const {
    bank_name,
    account_name,
    account_number,
    reference,
    amount,
    plan_name,
    duration_months,
    return_percentage,
  } = instructions || {};

  const formattedAmount = Number(amount || 0).toLocaleString();

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-instructions-title"
      data-testid="payment-instructions-modal"
    >
      <Card className="w-full max-w-lg my-8">
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-xl relative">
          <button
            type="button"
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={onClose}
            aria-label="Close"
            data-testid="payment-instructions-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div>
              <h2 id="payment-instructions-title" className="text-xl font-bold">
                Application submitted
              </h2>
              <p className="text-green-100 text-sm">Complete your investment below</p>
            </div>
          </div>
        </div>

        <CardContent className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-3">
            <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Transfer ₦{formattedAmount} to the account below.</p>
              <p className="text-amber-800 text-xs">
                Use the reference so we can match your payment. Your investment activates once our
                team confirms the transfer (usually within a few hours on business days).
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <CopyableRow icon={Building2} label="Bank" value={bank_name} />
            <CopyableRow icon={User} label="Account Name" value={account_name} />
            <CopyableRow icon={Hash} label="Account Number" value={account_number} mono />
            <CopyableRow icon={Wallet} label="Amount" value={`₦${formattedAmount}`} />
            <CopyableRow icon={Hash} label="Payment Reference" value={reference} mono />
          </div>

          {(plan_name || duration_months) && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <p className="font-semibold text-gray-700 mb-1">Investment summary</p>
              <div className="text-gray-600 space-y-0.5">
                {plan_name && <p>Plan: <strong>{plan_name}</strong></p>}
                {duration_months && (
                  <p>Duration: <strong>{duration_months} months</strong></p>
                )}
                {return_percentage && (
                  <p>Expected return: <strong>{return_percentage}%</strong></p>
                )}
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500 text-center">
            A copy of these details was also sent to your email.
          </p>

          <Button
            onClick={onClose}
            className="w-full bg-green-600 hover:bg-green-700"
            data-testid="payment-instructions-done-btn"
          >
            I&apos;ll pay now — take me to my dashboard <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentInstructions;
